import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { format, parseISO } from 'date-fns'
import { pl } from 'date-fns/locale'
import {
  AlertTriangle,
  ArrowLeft,
  Loader2,
  MapPinOff,
  Palmtree,
} from 'lucide-react'

import {
  absenceStatusLabel,
  formatEmploymentFraction,
  type VacationType,
} from '@czas/shared'

import Alert from '@/components/ui/Alert'
import { apiErrorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import {
  type MonthlyReportAbsenceDay,
  type MonthlyReportSession,
  type MonthlyReportUserData,
  useMonthlyReport,
} from '@/features/admin/use-reports'

const VACATION_TYPE_LABEL: Record<VacationType, string> = {
  WYPOCZYNKOWY: 'Wypoczynkowy',
  NA_ZADANIE: 'Na żądanie',
  BEZPLATNY: 'Bezpłatny',
  CHOROBOWY: 'Chorobowy (L4)',
  OPIEKA_NAD_DZIECKIEM: 'Opieka nad dzieckiem',
}

/**
 * Przypięta kolumna z datą — admin i manager czytają raport z telefonu,
 * gdzie tabela scrolluje w poziomie i bez tego nie wiadomo, którego dnia
 * dotyczy wiersz.
 */
const STICKY_COL =
  'sticky left-0 shadow-[1px_0_0_0_rgb(var(--border-subtle))]'

type DayRow =
  | { date: string; kind: 'session'; session: MonthlyReportSession }
  | { date: string; kind: 'absence'; absence: MonthlyReportAbsenceDay }

/**
 * Scala sesje pracy i dni nieobecności w jedną listę chronologiczną.
 *
 * Gdy ta sama data ma i sesję, i nieobecność, zostają OBA wiersze — dzień
 * liczy się wtedy podwójnie i tabela ma to pokazać, a nie ukryć. Sort jest
 * stabilny (ES2019+), więc sesje z jednego dnia zachowują kolejność po
 * godzinie startu, a nieobecność ląduje po nich.
 */
function buildDayRows(userData: MonthlyReportUserData): DayRow[] {
  const rows: DayRow[] = [
    ...userData.sessions.map(
      (session): DayRow => ({
        date: format(parseISO(session.startedAt), 'yyyy-MM-dd'),
        kind: 'session',
        session,
      }),
    ),
    ...userData.absences.map(
      (absence): DayRow => ({
        date: absence.date,
        kind: 'absence',
        absence,
      }),
    ),
  ]
  return rows.sort((a, b) => {
    const byDate = a.date.localeCompare(b.date)
    if (byDate !== 0) return byDate
    return (a.kind === 'absence' ? 1 : 0) - (b.kind === 'absence' ? 1 : 0)
  })
}

function parseYearMonth(value: string | null): string {
  if (value && /^\d{4}-(0[1-9]|1[0-2])$/.test(value)) return value
  return format(new Date(), 'yyyy-MM')
}

function yearMonthToDate(yearMonth: string): Date {
  const [y, m] = yearMonth.split('-').map(Number)
  return new Date(y!, m! - 1, 1)
}

function formatPolishMonth(yearMonth: string): string {
  return format(yearMonthToDate(yearMonth), 'LLLL yyyy', { locale: pl })
}

function formatHHMM(ms: number): string {
  const totalMinutes = Math.floor(ms / 60_000)
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function sessionStatusGps(s: MonthlyReportSession): {
  label: string
  warning: boolean
} {
  if (!s.startedOutside && !s.endedOutside) {
    return { label: 'W placówce', warning: false }
  }
  const parts: string[] = []
  if (s.startedOutside) {
    const dist =
      s.startedDistanceM !== null ? ` (~${s.startedDistanceM} m)` : ''
    parts.push(`Start poza${dist}`)
  }
  if (s.endedOutside) {
    const dist = s.endedDistanceM !== null ? ` (~${s.endedDistanceM} m)` : ''
    parts.push(`Koniec poza${dist}`)
  }
  return { label: parts.join(', '), warning: true }
}

export default function ReportUser() {
  const { userId } = useParams<{ userId: string }>()
  const [params] = useSearchParams()
  const yearMonth = parseYearMonth(params.get('ym'))

  const { data, isLoading, error } = useMonthlyReport(yearMonth)

  if (!userId) return <Navigate to="/admin/report" replace />

  if (isLoading) {
    return (
      <div className="text-sm text-text-muted inline-flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Wczytuję raport…
      </div>
    )
  }

  if (error) {
    return <Alert variant="error">{apiErrorMessage(error)}</Alert>
  }

  if (!data) return null

  const userData = data.users.find((u) => u.user.id === userId)

  if (!userData) {
    return (
      <div className="space-y-4">
        <Link
          to={`/admin/report?ym=${yearMonth}`}
          className="text-sm text-text-muted hover:text-text-primary inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Powrót do zespołu
        </Link>
        <Alert variant="info">
          Brak sesji tego pracownika w wybranym miesiącu.
        </Alert>
      </div>
    )
  }

  // Dni urlopu i L4 wplecione chronologicznie między sesje pracy. Bez tego
  // tabela pokazywała wyłącznie dni przepracowane i miesiąc z urlopem
  // wyglądał na dziurawy — dni nieobecności nigdzie nie było widać.
  const dayRows = buildDayRows(userData)
  const sessionDates = new Set(
    userData.sessions.map((s) => format(parseISO(s.startedAt), 'yyyy-MM-dd')),
  )

  return (
    <div className="space-y-6">
      <Link
        to={`/admin/report?ym=${yearMonth}`}
        className="text-sm text-text-muted hover:text-text-primary inline-flex items-center gap-1"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
        Powrót do zespołu
      </Link>

      <div>
        <h1 className="text-2xl font-semibold">{userData.user.name}</h1>
        <p className="text-sm text-text-muted capitalize">
          {userData.user.position?.name
            ? `${userData.user.position.name} · ${formatPolishMonth(yearMonth)}`
            : formatPolishMonth(yearMonth)}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard label="Łączny czas pracy">
          <span className="font-mono text-2xl">
            {userData.summary.totalHours.toFixed(2)}
          </span>{' '}
          <span className="text-text-muted">h</span>
          <div className="text-xs text-text-faint mt-1">
            ({formatHHMM(userData.summary.totalHours * 3_600_000)})
          </div>
        </SummaryCard>
        <SummaryCard label="Godziny z nieobecności">
          <span className="font-mono text-2xl">
            {userData.summary.vacationHours.toFixed(2)}
          </span>{' '}
          <span className="text-text-muted">h</span>
          <div className="text-xs text-text-faint mt-1">
            {userData.summary.vacationDays} dni · pełny dzień{' '}
            {userData.payroll.dailyHours.toFixed(2)} h
            {userData.payroll.vacationLines.some(
              (l) => l.percent !== 100 && l.percent > 0,
            ) && <> · L4 po 80%</>}
            {userData.summary.unpaidLeaveDays > 0 && (
              <> · {userData.summary.unpaidLeaveDays} d bezpłatnego</>
            )}
          </div>
        </SummaryCard>
        <SummaryCard label="Razem do rozliczenia">
          <span className="font-mono text-2xl">
            {userData.summary.creditedHours.toFixed(2)}
          </span>{' '}
          <span className="text-text-muted">h</span>
          <div className="text-xs text-text-faint mt-1">praca + urlop</div>
        </SummaryCard>
        <SummaryCard
          label="Sesje poza placówką"
          warning={userData.summary.outsideClinicCount > 0}
        >
          <span className="font-mono text-2xl">
            {userData.summary.outsideClinicCount}
          </span>
          <div className="text-xs text-text-faint mt-1">
            dni przepracowane: {userData.summary.daysWorked}
          </div>
        </SummaryCard>
      </div>

      <PayrollSection userData={userData} />

      {dayRows.length === 0 ? (
        <div className="rounded-card border border-border-subtle bg-bg-elevated p-8 text-center text-text-muted">
          Brak sesji i nieobecności w tym miesiącu.
        </div>
      ) : (
        <div className="rounded-card border border-border-subtle bg-bg-elevated overflow-hidden">
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full min-w-[620px] text-sm">
              <thead className="bg-bg-surface text-text-muted text-xs uppercase tracking-wider">
                <tr>
                  <th
                    className={cn(
                      'text-left px-4 py-3 font-medium',
                      STICKY_COL,
                      'bg-bg-surface z-20',
                    )}
                  >
                    Data
                  </th>
                  <th className="text-left px-4 py-3 font-medium">Start</th>
                  <th className="text-left px-4 py-3 font-medium">Koniec</th>
                  <th className="text-right px-4 py-3 font-medium">Czas</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {dayRows.map((row) => {
                  if (row.kind === 'absence') {
                    const a = row.absence
                    const conflict = sessionDates.has(a.date)
                    return (
                      <tr
                        key={`abs-${a.date}-${a.type}`}
                        className="border-t border-border-subtle bg-bg-surface/40"
                      >
                        <td
                          className={cn(
                            'px-4 py-3 font-mono',
                            STICKY_COL,
                            'bg-bg-surface/40 z-10',
                          )}
                        >
                          {a.date}
                        </td>
                        <td className="px-4 py-3 text-text-faint">—</td>
                        <td className="px-4 py-3 text-text-faint">—</td>
                        <td className="px-4 py-3 text-right font-mono">
                          {a.hours.toFixed(2)} h
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1.5 text-xs',
                              a.type === 'CHOROBOWY'
                                ? 'text-warning'
                                : a.percent === 0
                                  ? 'text-text-faint'
                                  : 'text-success',
                            )}
                          >
                            <Palmtree className="h-3 w-3" aria-hidden />
                            {absenceStatusLabel(a)}
                          </span>
                          {conflict && (
                            <span
                              className="ml-2 inline-flex items-center gap-1 text-xs text-danger"
                              title="Tego dnia jest też sesja pracy — dzień liczy się podwójnie. Sprawdź, czy wniosek albo sesja nie są pomyłką."
                            >
                              <AlertTriangle className="h-3 w-3" aria-hidden />
                              kolizja z sesją
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  }

                  const s = row.session
                  const start = parseISO(s.startedAt)
                  const end = s.endedAt ? parseISO(s.endedAt) : null
                  const status = sessionStatusGps(s)
                  const durationLabel =
                    s.durationMs !== null
                      ? `${(s.durationMs / 3_600_000).toFixed(2)} h`
                      : '—'
                  return (
                    <tr
                      key={s.id}
                      className="border-t border-border-subtle"
                    >
                      <td
                        className={cn(
                          'px-4 py-3 font-mono',
                          STICKY_COL,
                          'bg-bg-elevated z-10',
                        )}
                      >
                        {format(start, 'yyyy-MM-dd')}
                      </td>
                      <td className="px-4 py-3 font-mono">
                        {format(start, 'HH:mm')}
                      </td>
                      <td className="px-4 py-3 font-mono">
                        {end ? format(end, 'HH:mm') : '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {durationLabel}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            'inline-flex items-center gap-1.5 text-xs',
                            status.warning
                              ? 'text-warning'
                              : 'text-text-muted',
                          )}
                        >
                          {status.warning && (
                            <MapPinOff className="h-3 w-3" aria-hidden />
                          )}
                          {status.label}
                          {s.autoClosed && (
                            <span className="ml-1 inline-flex items-center gap-1 text-warning">
                              <AlertTriangle
                                className="h-3 w-3"
                                aria-hidden
                              />
                              auto
                            </span>
                          )}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              {/* Podsumowanie na końcu tabeli — kolumna „Czas" sumuje się do
                  godzin do rozliczenia, bo zawiera i pracę, i nieobecności. */}
              <tfoot className="bg-bg-surface font-medium">
                <tr className="border-t-2 border-border-strong">
                  <td
                    className={cn(
                      'px-4 py-3',
                      STICKY_COL,
                      'bg-bg-surface z-10',
                    )}
                  >
                    Razem
                  </td>
                  <td className="px-4 py-3" />
                  <td className="px-4 py-3" />
                  <td className="px-4 py-3 text-right font-mono">
                    {userData.summary.creditedHours.toFixed(2)} h
                  </td>
                  <td className="px-4 py-3 text-xs text-text-muted font-normal">
                    praca {userData.summary.totalHours.toFixed(2)} h
                    {userData.summary.vacationHours > 0 && (
                      <>
                        {' + '}nieobecności{' '}
                        {userData.summary.vacationHours.toFixed(2)} h
                      </>
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function PayrollSection({
  userData,
}: {
  userData: import('@/features/admin/use-reports').MonthlyReportUserData
}) {
  const { payroll, user } = userData

  if (payroll.hourlyRate === null) {
    return (
      <section className="rounded-card border border-border-subtle bg-bg-elevated p-4">
        <h3 className="text-sm font-semibold mb-2">
          Wynagrodzenie miesięczne (szacunek)
        </h3>
        <p className="text-sm text-text-muted">
          Brak ustawionej stawki godzinowej. Ustaw w{' '}
          <Link
            to={`/admin/users/${user.id}`}
            className="text-brand-accent hover:underline"
          >
            edycji pracownika
          </Link>
          .
        </p>
      </section>
    )
  }

  const fraction = user.employmentFraction ?? 1
  const rate = payroll.hourlyRate

  return (
    <section className="rounded-card border border-border-subtle bg-bg-elevated p-4">
      <h3 className="text-sm font-semibold mb-3">
        Wynagrodzenie miesięczne (szacunek)
      </h3>
      <div className="text-xs text-text-muted mb-3">
        Stawka:{' '}
        <strong className="text-text-primary">
          {rate.toLocaleString('pl-PL', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}{' '}
          zł/h
        </strong>
        {' · '}
        Pełny dzień:{' '}
        <strong className="text-text-primary">
          {payroll.dailyHours.toFixed(2)} h
        </strong>{' '}
        <span className="text-text-faint">
          (8h × etat {formatEmploymentFraction(fraction)})
        </span>
        {payroll.vacationLines.some((l) => l.type === 'CHOROBOWY') && (
          <span className="text-text-faint">
            {' · '}L4 zalicza 80% dnia, płatne pełną stawką od tych godzin
          </span>
        )}
      </div>

      <table className="w-full text-sm tabular-nums">
        <tbody>
          <tr>
            <td className="py-1">Praca</td>
            <td className="py-1 text-right text-text-muted">
              {payroll.workHours.toFixed(2)} h × {rate.toFixed(2)} zł
            </td>
            <td className="py-1 text-right font-medium">
              {payroll.workPay.toLocaleString('pl-PL', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
              zł
            </td>
          </tr>
          {/* Kwoty prosto z backendu — wcześniej liczone tu drugi raz, przez co
              pozycje potrafiły nie zsumować się do pokazanej sumy. */}
          {payroll.vacationLines.map((line) => (
            <tr key={line.type}>
              <td className="py-1">
                {VACATION_TYPE_LABEL[line.type]}
                {line.percent !== 100 && (
                  <span className="text-text-faint"> · {line.percent}% dnia</span>
                )}
              </td>
              <td className="py-1 text-right text-text-muted">
                {line.days} d ×{' '}
                {(payroll.dailyHours * (line.percent / 100)).toFixed(2)}h ×{' '}
                {rate.toFixed(2)} zł
              </td>
              <td className="py-1 text-right font-medium">
                {line.pay.toLocaleString('pl-PL', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{' '}
                zł
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-border-subtle">
            <td className="pt-2 font-semibold">Razem (netto)</td>
            <td></td>
            <td className="pt-2 text-right font-semibold text-base">
              {payroll.total.toLocaleString('pl-PL', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
              zł
            </td>
          </tr>
        </tfoot>
      </table>

      <p className="text-[11px] text-text-faint mt-4 leading-relaxed">
        Szacunek orientacyjny. Realne wynagrodzenie wg umowy i przepisów nalicza
        księgowa. Po 33 dniach L4 płatność przejmuje ZUS — koszty firmy mogą
        być niższe niż szacunek.
      </p>
    </section>
  )
}

function SummaryCard({
  label,
  children,
  warning = false,
}: {
  label: string
  children: React.ReactNode
  warning?: boolean
}) {
  return (
    <div
      className={cn(
        'rounded-card border bg-bg-elevated p-4',
        warning ? 'border-warning/30' : 'border-border-subtle',
      )}
    >
      <div
        className={cn(
          'text-xs uppercase tracking-wider mb-2',
          warning ? 'text-warning' : 'text-text-muted',
        )}
      >
        {label}
      </div>
      <div className={cn(warning ? 'text-warning' : 'text-text-primary')}>
        {children}
      </div>
    </div>
  )
}
